'use client'

import { useRouter, usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useState, useTransition, useEffect, useCallback } from 'react'
import { useAuthStore } from '@/stores'
import { NAV_MENU_ITEMS } from '@/config/navigation'
import { useNotificationCount } from '@/hooks/useNotificationCount'
import { useBuilderUnsavedCheck } from '@/hooks/useBuilderUnsavedCheck'
import { LucideIcon } from '@/components/ui/Icons'
import { Spinner } from '@/components/ui/Loading'
import { ChevronsLeft, ChevronsRight } from 'lucide-react'
import { ConfirmationDialog } from '@/components/ui/Dialog'
import { ImageLibraryModal } from '@/components/ImageLibrary'
import MenuItem from './MenuItem'
import AccountSection from './AccountSection'

interface NavigationProps {
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  /** Open library modal with export dialog (from OAuth callback in layout) */
  openLibraryWithExport?: boolean
  /** Callback when library export has been opened */
  onLibraryExportOpened?: () => void
}

export default function Navigation({
  isCollapsed = false,
  onToggleCollapse,
  openLibraryWithExport = false,
  onLibraryExportOpened,
}: NavigationProps) {
  const router = useRouter()
  const pathname = usePathname()
  const tNav = useTranslations('navigation')
  const { user } = useAuthStore()
  const unreadCount = useNotificationCount()
  const { hasUnsavedBuilderChanges } = useBuilderUnsavedCheck()
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = useState(false)
  const [isLibraryOpen, setIsLibraryOpen] = useState(false)
  const [autoOpenExport, setAutoOpenExport] = useState(false)
  const [pendingNavigationPath, setPendingNavigationPath] = useState<
    string | null
  >(null)
  const [, startTransition] = useTransition()
  const [loadingPath, setLoadingPath] = useState<string | null>(null)

  // Handle openLibraryWithExport prop from layout (OAuth callback handled in layout)
  // The layout captures OAuth params before the loading check, so this works reliably
  useEffect(() => {
    if (openLibraryWithExport) {
      setAutoOpenExport(true)
      setIsLibraryOpen(true)
      // Notify layout that we've handled it
      onLibraryExportOpened?.()
    }
  }, [openLibraryWithExport, onLibraryExportOpened])

  // Stable callbacks for ImageLibraryModal
  const handleLibraryClose = useCallback(() => {
    setIsLibraryOpen(false)
    setAutoOpenExport(false) // Reset autoOpenExport when modal closes
  }, [])
  const handleLibrarySelect = useCallback(() => {
    // In general mode, we don't need to do anything with selection
  }, [])

  // Reset loading state when pathname changes (navigation completed)
  useEffect(() => {
    if (loadingPath) {
      setLoadingPath(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const handleNavigate = (path: string) => {
    if (hasUnsavedBuilderChanges()) {
      setPendingNavigationPath(path)
      setIsUnsavedModalOpen(true)
      return
    }
    setLoadingPath(path)
    startTransition(() => {
      router.push(path)
    })
  }

  const confirmNavigation = () => {
    setIsUnsavedModalOpen(false)
    if (pendingNavigationPath) {
      setLoadingPath(pendingNavigationPath)
      startTransition(() => {
        router.push(pendingNavigationPath)
      })
      setPendingNavigationPath(null)
    }
  }

  // Check if the current path matches the menu item
  // Only exact matches are considered "active" (which disables the button)
  // This allows clicking "My Invitations" from invitation detail pages to go back to the list
  const isActive = (path: string) => {
    // Only exact match disables the navigation item
    return pathname === path
  }

  // Check if user is in the invitation creation flow (digital or stationery)
  const isInCreationFlow =
    pathname.startsWith('/designs') ||
    pathname.startsWith('/builder') ||
    pathname.startsWith('/stationery-builder')

  // Separate Create Invitation from other menu items
  const createInvitationItem = NAV_MENU_ITEMS.find(
    (item) => item.id === 'create-invitation'
  )
  const otherMenuItems = NAV_MENU_ITEMS.filter(
    (item) => item.id !== 'create-invitation'
  )

  return (
    <>
      {/* Desktop Navigation Drawer */}
      <aside
        className={`hidden md:flex bg-card border-r border-border flex-col h-full transition-[width] duration-200 ease-out will-change-[width] ${
          isCollapsed ? 'w-0 overflow-hidden border-r-0' : 'w-56'
        }`}
      >
        {/* Account Section */}
        <AccountSection user={user} />

        {/* Create Invitation - Special Section */}
        {createInvitationItem && (
          <div className="px-3 py-3 border-y border-border">
            <button
              onClick={() => {
                if (
                  !isInCreationFlow &&
                  createInvitationItem.path &&
                  loadingPath !== createInvitationItem.path
                ) {
                  handleNavigate(createInvitationItem.path)
                }
              }}
              disabled={loadingPath === createInvitationItem.path}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                isInCreationFlow
                  ? 'bg-primary/15 text-primary border-l-2 border-primary cursor-default'
                  : loadingPath === createInvitationItem.path
                    ? 'bg-primary text-primary-foreground cursor-wait opacity-90 shadow-md border border-primary/20'
                    : 'bg-primary text-primary-foreground hover:bg-primary-hover hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 cursor-pointer shadow-md border border-primary/20'
              }`}
            >
              {loadingPath === createInvitationItem.path ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                <LucideIcon
                  name={createInvitationItem.icon}
                  className="w-4 h-4 shrink-0"
                />
              )}
              <span className="whitespace-nowrap">
                {tNav(createInvitationItem.translationKey)}
              </span>
            </button>
          </div>
        )}

        {/* Menu and Logout Button - Scrollable */}
        <div className="flex-1 overflow-y-auto flex flex-col">
          <nav className="flex-1 px-3 py-4">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2 whitespace-nowrap">
              {tNav('menu')}
            </h3>
            <div className="space-y-1">
              {otherMenuItems.map((item) => (
                <MenuItem
                  key={item.id}
                  icon={item.icon}
                  label={tNav(item.translationKey)}
                  isActive={item.path ? isActive(item.path) : false}
                  onClick={() => {
                    if (item.action === 'openLibrary') {
                      setIsLibraryOpen(true)
                    } else if (item.path) {
                      handleNavigate(item.path)
                    }
                  }}
                  showBadge={item.id === 'notifications' && unreadCount > 0}
                  badgeCount={
                    item.id === 'notifications' ? unreadCount : undefined
                  }
                  isLoading={item.path ? loadingPath === item.path : false}
                />
              ))}
            </div>
          </nav>
        </div>
      </aside>

      {/* Collapse/Expand Toggle Button - Fixed on edge */}
      {onToggleCollapse && (
        <button
          onClick={onToggleCollapse}
          className={`hidden md:flex absolute top-1/2 -translate-y-1/2 z-10 items-center justify-center w-6 h-14 bg-card border border-border border-l-0 rounded-r-lg text-muted-foreground hover:text-primary hover:bg-muted shadow-sm transition-[left,colors] duration-200 will-change-[left] cursor-pointer ${
            isCollapsed ? 'left-0' : 'left-56'
          }`}
          aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {isCollapsed ? (
            <ChevronsRight className="w-4 h-4" />
          ) : (
            <ChevronsLeft className="w-4 h-4" />
          )}
        </button>
      )}

      {/* Unsaved Changes Confirmation Modal */}
      <ConfirmationDialog
        isOpen={isUnsavedModalOpen}
        onClose={() => {
          setIsUnsavedModalOpen(false)
          setPendingNavigationPath(null)
        }}
        onConfirm={confirmNavigation}
        title="Unsaved Changes"
        description="You have unsaved changes that will be lost. Do you want to leave without saving?"
        confirmText="Leave"
        cancelText="Stay"
        variant="warning"
      />

      {/* My Library Modal - General mode */}
      {isLibraryOpen && (
        <ImageLibraryModal
          isOpen={isLibraryOpen}
          onClose={handleLibraryClose}
          context="general"
          onSelect={handleLibrarySelect}
          invitationId={undefined}
          isAuthenticated={!!user}
          autoOpenExport={autoOpenExport}
        />
      )}
    </>
  )
}
